import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBrandById, updateBrand } from '../../services/companyAuthService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import categoriesData from "../../../data/categories.json"
import countriesData from "../../../data/countries.json";

const EditBrand = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    brandName: '',
    country: '',
    category: '',
    subcategory: '',
  });

  const [categories] = useState(categoriesData);
  const [subcategories, setSubcategories] = useState([]);
  const [allCountries, setAllCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [isBrandActive, setIsBrandActive] = useState(true);

  useEffect(() => {
    fetchBrandDetails();
    // Extract country names and sort them alphabetically
    const countryNames = countriesData
      .map((c) => c.name)
      .sort((a, b) => a.localeCompare(b));
    setAllCountries(countryNames);
  }, [id]);

  useEffect(() => {
    // Update subcategories when category changes
    if (formData.category) {
      const selectedCategory = categories.find(cat => cat.category === formData.category);
      setSubcategories(selectedCategory ? selectedCategory.subcategories : []);
    }
  }, [formData.category, categories]);

  const fetchBrandDetails = async () => {
    try {
      setLoading(true);
      const response = await getBrandById(id);
      if (response.success) {
        setFormData({
          brandName: response.brand.brandName,
          country: response.brand.country,
          category: response.brand.category,
          subcategory: response.brand.subcategory,
        });
        setIsBrandActive(Boolean(response.brand?.isActive));
      } else {
        toast.error("Failed to fetch brand details");
        navigate('/company/dashboard/brands/');
      }
    } catch (error) {
      console.error("Error fetching brand:", error);
      toast.error(error.message || "Error fetching brand details");
      navigate('/company/dashboard/brands/');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle country input change
  const handleCountryChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      country: value
    }));

    if (value === "") {
      setFilteredCountries([]);
      setShowCountryDropdown(false);
    } else {
      const filtered = allCountries.filter((c) =>
        c.toLowerCase().startsWith(value.toLowerCase())
      );
      setFilteredCountries(filtered);
      setShowCountryDropdown(filtered.length > 0);
    }
  };

  // Select country from dropdown
  const handleSelectCountry = (selectedCountry) => {
    setFormData(prev => ({
      ...prev,
      country: selectedCountry
    }));
    setFilteredCountries([]);
    setShowCountryDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!isBrandActive) {
      toast.error('Inactive brands cannot be edited');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await updateBrand(id, formData);

      if (response.success) {
        toast.success("Brand updated successfully");
        navigate('/company/dashboard/brands/');
      } else {
        toast.error(response.message || "Failed to update brand");
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      toast.error(error.message || "Failed to update brand");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>

        <div className="flex justify-center items-center min-h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Back Button */}
      <div>
        <button
          onClick={() => navigate("/company/dashboard/brands/")}
          className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-orange-200 transition-colors duration-200"
        >
          ← Back
        </button>
      </div>
      <div className="h-100 bg-gray-100 flex lg:items-center justify-center px-4">
        <div className="bg-white w-full max-w-4xl p-6 sm:p-8 lg:p-10 rounded-xl shadow-md mt-12">

          <h2 className="text-2xl font-bold text-black text-center mb-6">Edit Brand</h2>
          {!isBrandActive && (
            <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm text-center">
              This brand is inactive and cannot be edited.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Brand Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Brand Name
              </label>
              <input
                type="text"
                name="brandName"
                value={formData.brandName}
                onChange={handleChange}
                disabled={!isBrandActive}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-orange-500 focus:outline-none focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>

            {/* Country with Searchable Dropdown */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700">
                Country
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleCountryChange}
                placeholder="Search and select country"
                disabled={!isBrandActive}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-orange-500 focus:outline-none focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
              {showCountryDropdown && isBrandActive && (
                <ul className="absolute z-10 bg-white border border-gray-300 rounded-md shadow-md mt-1 w-full max-h-40 overflow-y-auto">
                  {filteredCountries.map((country, index) => (
                    <li
                      key={index}
                      onClick={() => handleSelectCountry(country)}
                      className="px-3 py-2 hover:bg-orange-100 cursor-pointer text-sm"
                    >
                      {country}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                disabled={!isBrandActive}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-orange-500 focus:outline-none focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.category} value={cat.category}>
                    {cat.category}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategory */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Subcategory
              </label>
              <select
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
                disabled={!isBrandActive}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-orange-500 focus:outline-none focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              >
                <option value="">Select Subcategory</option>
                {subcategories.map((sub) => (
                  <option key={sub} value={sub}>
                    {sub}
                  </option>
                ))}
              </select>
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => navigate('/company/dashboard/brands/')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !isBrandActive}
                className={`lg:px-4 px-2 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 ${isSubmitting || !isBrandActive ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {isSubmitting ? 'Updating...' : 'Update Brand'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default EditBrand; 